import { useEffect, useRef, useState } from 'react';
import { Animated, Platform } from 'react-native';
import { useReducedMotion } from 'react-native-reanimated';

import { DURATION } from '@/constants/animation';

/** Out and back in, so the pair costs one standard transition rather than two. */
const FADE_LEG_MS = DURATION.normal / 2;

/** React Native's native driver cannot run under react-native-web; opacity there stays on JS. */
const USE_NATIVE_DRIVER = Platform.OS !== 'web';

type Crossfade<T> = {
  opacity: Animated.Value;
  /**
   * The content to render, which trails `value` for the length of the fade-out
   * so the outgoing item is still the one on screen while it leaves.
   */
  displayed: T;
  /** The key `displayed` belongs to, for a caller that has to name what it is showing. */
  displayedKey: string | null;
  /** A swap is in flight, and what is painted no longer matches what the caller passed. */
  isSwapping: boolean;
};

/**
 * Fades one piece of content out and the next one in, holding the outgoing copy
 * until it has actually gone.
 *
 * Built on core `Animated` rather than Reanimated, which is this repo's stated
 * preference for new motion. Reanimated's `entering`/`exiting` do not fire under
 * react-native-web at all, and the one place this is used on native nests inside
 * a view whose own opacity is already animated by a gesture — two libraries
 * writing one property is worse than one library doing both.
 *
 * `key` decides when to swap; `value` is only read at the moment of a swap. A
 * caller that rebuilds `value` every render therefore does not restart the fade,
 * which is what lets the content be a props object rather than a memo.
 *
 * Under reduced motion the swap is immediate. The content still changes — only
 * the travel is dropped.
 *
 * `isSwapping` exists because a fade is a window where the caller's own state has
 * already moved on: a press landing mid-fade would be read against the incoming
 * item while the reader is still looking at the outgoing one. Callers are
 * expected to stop accepting input for its duration rather than resolve that
 * mismatch after the fact.
 */
export function useCrossfade<T>(key: string | null, value: T): Crossfade<T> {
  const opacity = useRef(new Animated.Value(1)).current;

  const [displayed, setDisplayed] = useState<{ key: string | null; value: T }>({ key, value });

  const [isSwapping, setIsSwapping] = useState(false);

  const reduceMotion = useReducedMotion();

  const latestValue = useRef(value);

  latestValue.current = value;

  const displayedKey = useRef(key);

  useEffect(() => {
    if (key === displayedKey.current) {
      return;
    }

    const commit = () => {
      displayedKey.current = key;

      setDisplayed({ key, value: latestValue.current });
    };

    // Nothing was on screen to fade out of, so there is no crossfade to run.
    if (reduceMotion || displayedKey.current === null || key === null) {
      commit();

      opacity.setValue(1);

      setIsSwapping(false);

      return;
    }

    setIsSwapping(true);

    Animated.timing(opacity, {
      toValue: 0,
      duration: FADE_LEG_MS,
      useNativeDriver: USE_NATIVE_DRIVER,
    }).start(({ finished }) => {
      if (!finished) {
        return;
      }

      commit();

      Animated.timing(opacity, {
        toValue: 1,
        duration: FADE_LEG_MS,
        useNativeDriver: USE_NATIVE_DRIVER,
      }).start(() => setIsSwapping(false));
    });
  }, [key, reduceMotion, opacity]);

  return { opacity, displayed: displayed.value, displayedKey: displayed.key, isSwapping };
}
